import * as React from "react"
import { cn } from "@/lib/utils"
import { roundToPeso } from "@/lib/roundToPeso"
import { Input } from "@/components/ui/input"

export interface CurrencyInputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
    value?: number | string
    onValueChange: (value: number) => void
}

export const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
    ({ className, value, onValueChange, ...props }, ref) => {
        const [displayValue, setDisplayValue] = React.useState("")
        const [isFocused, setIsFocused] = React.useState(false)

        // Helper to format number to AR locale
        const formatCurrency = (val: number | string | undefined) => {
            if (val === undefined || val === "" || val === null) return ""
            const num = Number(val)
            if (isNaN(num)) return ""
            return num.toLocaleString("es-AR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        }

        // Sync external value changes when not focused
        React.useEffect(() => {
            if (!isFocused) {
                setDisplayValue(formatCurrency(value))
            }
        }, [value, isFocused])

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(true)
            // When focused, show raw number for editing
            if (value !== undefined && value !== null) {
                setDisplayValue(value.toString().replace('.', ','))
            }
            // Auto-select the content for easier editing
            setTimeout(() => {
                e.target.select()
            }, 0)
            props.onFocus?.(e)
        }

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(false)
            const current = Number(value)
            const n = Number.isFinite(current) ? current : 0
            const rounded = roundToPeso(n)
            if (rounded !== n) {
                onValueChange(rounded)
            }
            props.onBlur?.(e)
        }

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value

            // Update display value immediately while typing
            setDisplayValue(inputValue)

            // Smart parsing strategy for mixed input (dots and commas)
            // 1. If comma exists, treat comma as decimal and dots as thousands (standard ES-AR)
            // 2. If NO comma exists but DOT exists:
            //    a) If multiple dots, treat as thousands (e.g. 1.000.000)
            //    b) If single dot, check position.
            //       - If it looks like decimal (e.g. 123.45), treat as decimal.
            //       - If user explicitly intended dot as decimal, let's look at the last separator.

            let numValue: number;

            // Remove all non-numeric chars except . and ,
            const raw = inputValue.replace(/[^\d.,-]/g, "");

            // Check if we have a comma (standard decimal)
            if (raw.includes(',')) {
                // Remove dots (thousands), replace comma with dot (decimal)
                const clean = raw.replace(/\./g, "").replace(",", ".");
                numValue = parseFloat(clean);
            } else {
                // No comma. Check dots.
                const dotCount = (raw.match(/\./g) || []).length;
                if (dotCount === 1) {
                    // Ambiguous. "1.000" (1000) vs "10.50" (10.5).
                    // In AR, dot is thousand. But users often type dot for decimal.
                    // Heuristic: If we are typing, usually the LAST char being a dot means decimal start.
                    // But here we get the full string.
                    // Let's assume dot is DECIMAL if the user provided it in a context where it makes sense?
                    // Actually, safer to stick to locale BUT allow dot to act as comma if it's the valid decimal separator position.

                    // User Request: "the importe field i type 6962639.10 which should cast to ... .10 cents"
                    // User typed '.' and it got stripped.
                    // The previous code: inputValue.replace(/\./g, "").replace(",", ".")
                    // 6962639.10 -> 696263910 (Huge number!)

                    // FIX: If the input has a dot and NO commas, and the dot is near the end (2 decimals), treat as decimal.
                    const parts = raw.split('.');
                    if (parts.length === 2) {
                        // Check decimal part length
                        // 6962639.10 -> parts[1] is '10'
                        // 1.000 -> parts[1] is '000' (likely thousand)

                        // If we just blindly replace dot with decimal, 1.000 becomes 1.0 (1).
                        // That breaks "1.000" meaning 1000.
                        // But allows "10.50".

                        // Hybrid approach: replace the LAST dot with a decimal point IF there are no commas.
                        // But what if 1.000.000? Then we have 2 dots. Logic below handles dotCount === 1.

                        // If user types dot, we convert it to comma in display immediately? No, that interferes.
                        // Better: Treat dot as decimal.
                        // Let's force consistent behavior:
                        // Parse standard string: replace commas with dots.
                        // But wait, our display format is 1.000,00.
                        // If we edit: 1000000,00

                        // Let's change the replacement logic: 
                        // Treat the LAST separator (dot or comma) as decimal if it makes sense?
                        // Or just follow user preference?
                        // User typed 6962639.10.

                        // Let's try: Replace keys.
                        // If input has no commas, replace dots with decimals?
                        // 1.000 -> 1.0 -> 1. (Issue: 1.000 is 1000 in AR)

                        // Safe fix for this specific user issue:
                        // If string looks like English float (digits . digits), parse as float.
                        numValue = parseFloat(raw);
                        // 1.000 parses as 1.
                        // 6962639.10 parses as 6962639.1.

                        // Wait, parseFloat("1.000") is 1.
                        // parseFloat("1000") is 1000.

                        // If the user INTENDS dot to be decimal, we must respect it.
                        // If the user INTENDS dot to be thousand, we must respect it.
                        // We can't know for sure without context.

                        // However, the previous logic `replace(/\./g, "")` was DESTRUCTIVE. It turned 1.50 into 150.
                        // That is definitely wrong for a decimal dot.

                        // Compromise:
                        // If `raw` matches strictly regex like `^\d+\.\d{1,2}$` (digits, dot, 1 or 2 digits), treat as decimal.
                        // "1.000" (3 digits) -> Thousand.
                        // "10.5" (1 digit) -> Decimal.
                        // "10.50" (2 digits) -> Decimal.
                        // "6962639.10" -> Decimal.

                        if (/^\d+\.\d{1,2}$/.test(raw)) {
                            // Treat dot as decimal
                            numValue = parseFloat(raw);
                        } else {
                            // Treat as thousand (strip it)
                            numValue = parseFloat(raw.replace(/\./g, ""));
                        }
                    } else {
                        // Multiple dots? 1.000.000 -> Strip all
                        numValue = parseFloat(raw.replace(/\./g, ""));
                    }
                } else {
                    // Multiple dots or no dots
                    numValue = parseFloat(raw.replace(/\./g, ""));
                }
            }

            if (!isNaN(numValue)) {
                onValueChange(numValue)
            } else {
                if (inputValue === "") onValueChange(0)
            }
        }

        return (
            <Input
                {...props}
                ref={ref}
                type="text"
                inputMode="decimal"
                value={displayValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                className={cn("text-right font-mono", className)}
            />
        )
    }
)
CurrencyInput.displayName = "CurrencyInput"
